import { TextField, NumberField, FunctionField, usePermissions, useRecordContext, TopToolbar, useTranslate, Edit } from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { useTheme, useMediaQuery, Box, Typography, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import CustomList from '@/components/global/list/CustomList.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import { useDrawer } from '@/composables/useDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { accessKeyStatusChoices } from '@/components/global/options/selectOptions.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { useMemo } from 'react';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { styled } from '@mui/material/styles';
import { globalStyles } from '@/themes/commonStyles/index.js';
import { AccessKeyForm } from '@/components/accessKeys/forms/AccessKeyForm.jsx';
import { EmptyField } from '@/components/accessKeys/fields/EmptyField.jsx';
import { StatusField } from '@/components/accessKeys/fields/StatusField.jsx';
import { AccessKeyShow } from '@/components/accessKeys/AccessKeyShow.jsx';
import { RevokeActionButton, RotateActionButton } from '@/components/accessKeys/buttons/actionButtons.jsx';
import { AccessKeyStatisticList } from '@/components/analytics/statisticList.jsx';
import { AccessKeyBulkActionButtons } from '@/components/accessKeys/BulkAccessKeyActions.jsx';

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <>
            {!isMobile && (
                <TopToolbar>
                    <SearchFilterInput />
                    <CustomFilterButton />
                    <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
                    {permissions.isAdmin && (
                        <CustomToolbarActionsPopover>
                            <CustomCreateButton label={translate('resources.accessKey.labels.create')} />
                        </CustomToolbarActionsPopover>
                    )}
                </TopToolbar>
            )}
        </>
    );
};

const MoreActionsPopover = ({ hideEdit }) => {
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <CustomMorePopover record={record}>
            <RevokeActionButton id={record.id} />
            <RotateActionButton id={record.id} />
            {!hideEdit && (
                <CustomEditModalButton label={translate('resources.accessKey.labels.edit')} onOpen={() => openDrawer(record.id, 'access-key-edit')} />
            )}
        </CustomMorePopover>
    );
};

export const AccessKeyList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const filters = useMemo(
        () => [
            <CustomSelectArrayInput
                key='status_in'
                source='status_in'
                label={translate('resources.accessKey.labels.status')}
                choices={accessKeyStatusChoices(translate)}
                fullWidth
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='device->id_in'
                source='device->id_in'
                label={translate('resources.accessKey.labels.serialNumber')}
                reference='device'
                optionText={choice => choice.serialNumber}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='currentKeyExpires'
                source='currentKeyExpires_range'
                label={translate('resources.accessKey.labels.currentKeyExpires')}
                type='date'
                allowFutureDates
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, translate],
    );

    if (isLoading) return null;

    return (
        <CustomList filters={filters} actions={<ListActions />} sort={{ field: 'updatedAt', order: 'DESC' }} addLastColumnShadow={true} empty={false}>
            <AccessKeyStatisticList />
            <CustomDatagridConfigurable
                header={<CustomDatagridHeader />}
                rowClick={id => {
                    openDrawer(id, 'access-key-edit');
                    return false;
                }}
                bulkActionButtons={<AccessKeyBulkActionButtons />}
            >
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.serialNumber')} />}
                    sortBy='device.serialNumber'
                    render={record => (
                        <TextField
                            source='device.serialNumber'
                            onClick={e => {
                                e.stopPropagation();
                                openDrawer(record.deviceId, 'device-show');
                            }}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        />
                    )}
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.keyPreview')} />}
                    sortBy='keyPreview'
                    render={() => <TextField source='keyPreview' />}
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.status')} />}
                    sortBy='status'
                    render={record => <StatusField record={record} />}
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.lastSeenAt')} />}
                    source='lastSeenAt'
                    sortBy='lastSeenAt'
                    render={record =>
                        record.lastSeenAt ? (
                            <Tooltip
                                title={
                                    formatIntervalSince(record.lastSeenAt)
                                        ? `${formatIntervalSince(record.lastSeenAt)} ${translate('notification.timeAgo')}`
                                        : ''
                                }
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <span>{format(new Date(record.lastSeenAt), 'dd.MM.yyyy HH:mm')}</span>
                            </Tooltip>
                        ) : (
                            <EmptyField />
                        )
                    }
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.currentKeyExpires')} />}
                    source='currentKeyExpires'
                    sortBy='currentKeyExpires'
                    render={record =>
                        record.currentKeyExpires ? <span>{format(new Date(record.currentKeyExpires), 'dd.MM.yyyy HH:mm')}</span> : <EmptyField />
                    }
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('resources.accessKey.labels.daysUntilRotation')} />}
                    sortBy='daysUntilRotation'
                    render={record =>
                        record.daysUntilRotation !== null && record.daysUntilRotation !== undefined ? (
                            <NumberField source='daysUntilRotation' />
                        ) : (
                            <EmptyField />
                        )
                    }
                />
                <FunctionField
                    label={<TableHeaderCellWithSubtitle title={translate('common.options')} />}
                    render={() => (
                        <Box onClick={e => e.stopPropagation()}>
                            <MoreActionsPopover permissions={permissions} />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

const StyledForm = styled(Box)(globalStyles.accessKeyForm);

export const AccessKeyCreate = ({ handleClose = () => {} }) => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <StyledForm>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.accessKey.labels.create')}</Typography>
                    <Box>
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <CustomCreate handleClose={handleClose}>
                <Box className='padding-wrapper'>
                    <AccessKeyForm />
                </Box>
            </CustomCreate>
        </StyledForm>
    );
};

export const AccessKeyEdit = ({ handleClose = () => {}, elementId }) => {
    const { isLoading } = usePermissions();
    const translate = useTranslate();

    if (isLoading) return null;

    return (
        <StyledForm>
            <Edit
                id={elementId}
                resource='access-key'
                actions={false}
                mutationMode='pessimistic'
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.accessKey.labels.edit')}</Typography>
                        <Box>
                            <MoreActionsPopover hideEdit />
                            <CustomCloseButton onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>
                <Box
                    className='popup-wrapper border-bottom'
                    sx={{
                        padding: '0 !important',
                    }}
                >
                    <Box className='popup-box'>
                        <AccessKeyShow elementId={elementId} />
                    </Box>
                </Box>
            </Edit>
        </StyledForm>
    );
};
